import type { AppIconName } from "./AppIcon";
import type { KindTabKey } from "../api/documentWorkflowTypes";
import { TRANSMITTALS_PATH } from "./constants";
import { PRIORITY_OPTIONS } from "../components/documents/rfi/rfiDraft";

export type DocumentModuleGroup = "overview" | "workflows" | "library";
/**
 * What a module needs on the tenant: `projectManagement` the workspace
 * (`features.project_management.enabled`); `primepointRecords` Primepoint as the system of record
 * (`features.primepoint_records.enabled`) inside the workspace, or, for a kind the project's
 * provider takes (punch items, issues), the workspace alone (`moduleRequirementMet`).
 */
export type DocumentModuleRequirement =
    "projectManagement" | "primepointRecords" | "inspections" | "ifoa" | "primeContract";

/** A sub-collection in the rail, reached by `?tab=` on the parent route. */
export interface DocumentModuleChild {
    tab: string;
    label: string;
    /** Trailing count source; `packages` reads the submittal package total. */
    count?: "packages";
}

/** Legacy library totals (`useDocumentCounts`) a module can read. */
export type LegacyCountKey =
    | "sheets"
    | "specifications"
    | "rfi"
    | "submittal"
    | "contracts"
    | "code"
    | "ifoa"
    | "primeContract"
    | "schedule"
    | "supplemental";

/**
 * How the rail and overview count a module. `native` reads the PM counts endpoint by
 * this module's id (or `keys`); `records` reads the same endpoint by the module's id on
 * every tenant, Project Management on or off: the records its register lists, provider
 * mirrors included; `combined` adds a legacy library total to the native count; `legacy`
 * reads the library total alone, plus the available `sections` when `withSections` is
 * set; `folders` and `provider` are supplied by the folder library and provider file
 * stores instead of the category list.
 */
export type DocumentCountSource =
    | { source: "none" }
    | { source: "my-work" }
    | { source: "photos" }
    | { source: "folders" }
    | { source: "provider" }
    | { source: "native"; keys?: readonly string[] }
    | { source: "records" }
    | { source: "legacy"; key: LegacyCountKey; withSections?: boolean }
    | { source: "combined"; key: LegacyCountKey };

/** The record view body a kind renders with (`ControlRecordView`). */
export type RecordBodyPreset =
    | "rfi"
    | "submittal"
    | "transmittal"
    | "provision"
    | "fields"
    | "dailyReport"
    | "meeting"
    | "form"
    /** The HDH inspection request: rounds and sections; a v1-pinned record keeps `fields`' body. */
    | "inspection"
    /** The HDH DDNC: its closing path and party statuses (`ddnc/`). */
    | "ddnc";
/**
 * The register a kind lists in. `field` is the server-paged field register
 * (`ProjectManagementRegister`), laid out by the kind's `fieldRegister` and `statuses`. `log` is
 * the server-paged editable grid (`ddnc/DdncLog`), laid out by the kind's `logRegister`.
 */
export type RecordRegisterPreset = "control" | "workspace" | "forms" | "field" | "log";
/**
 * The inspection request register's own columns (HDH contract §4.1, the S2 order), read from the
 * `inspection` row part and drawn by `inspections/register/inspectionColumns.tsx`.
 */
export type InspectionRegisterColumn =
    "next_action" | "company" | "inspector" | "requested" | "sections" | "lbs" | "sheets" | "flags";
/** A column of the field register after Status · No. · Title, by the summary field it reads. */
export type FieldRegisterColumn =
    | "trade"
    | "type"
    | "location"
    | "assignee"
    | "due"
    | "date"
    | "workers"
    | "hours"
    | "open_actions"
    | "author"
    | "attendees"
    | InspectionRegisterColumn;
/**
 * A filter of the field register's panel. Free text: `trade`, `type`, `level`, `location`,
 * `lbs_prefix` (a full LBS code prefix, HDH §4.3) and `ddnc_area` (an item of a DDNC's Area list,
 * HDH §4.3). A choice the register summary counts: `court` (its holders) and a company workflow
 * field, `field.<key>` (its companies); a choice field's `field.<key>` takes the field's options.
 * `show` chooses one of the layout's `show` views.
 */
export type FieldRegisterFilter =
    | "trade"
    | "type"
    | "level"
    | "location"
    | "court"
    | "lbs_prefix"
    | "ddnc_area"
    | "show"
    | `field.${string}`;
/** A tab of a kind with its own (HDH §4.3): `all` lists every row and sends no `tab`. */
export type FieldRegisterTabKey = KindTabKey | "closed" | "all";
/** The inspection request register's child rows: its rounds (`inspectionColumns.tsx`). */
export type InspectionChildRows = "rounds";
/**
 * Rows the field register draws under a row, with the same columns: an inspection request's
 * rounds. `fieldRegisterColumns`' catalogue reads them (`fieldChildRows`).
 */
export type FieldRegisterChildRows = InspectionChildRows;
/**
 * A column of the `log` register (HDH contract §5.4), by where its value lives: the record itself
 * (number, created, description), a workflow field, the `ddnc` row part, or the `court` part.
 * `key` is the server's name for the column: the row's `edits` part lists and locks columns by
 * it (`register_ddnc_rows.edits`), as the DDNC export names them (`register_ddnc_export`).
 * The header is the template's field label where the column is a field (tenant data); `label`
 * is the fallback, and the only header of the record and derived columns. `locked` columns are
 * never edited in place; why a cell is locked is always the row's `edits.locked[key]`.
 */
export interface LogColumn {
    key: string;
    source: "record" | "field" | "ddnc" | "court";
    label: string;
    locked?: true;
    /** What a change in this column does beyond its value, said in the editor before Save. */
    hint?: string;
}
/**
 * The `log` register's layout (HDH §5.4): its columns, how "+ Add row" logs a row (the draft
 * action that logs it, and the one field it asks for first), and which of the kind's tabs the
 * footer counts beside the rows shown ("5 shown · 4 open · 1 closed").
 */
export interface LogRegister {
    columns: readonly LogColumn[];
    /** The action that logs a bare row, the one field it asks, and the title its draft is created
     *  with (the engine asks every record for a subject; contract §2.4.3 "title defaults"). */
    addRow?: { action: string; field: string; title: string };
    footerTabs?: readonly { key: FieldRegisterTabKey; word: string }[];
}
/** How the field register lays a kind out. Static layout only: rows, totals and actions are the server's. */
export interface FieldRegisterLayout {
    columns: readonly FieldRegisterColumn[];
    /**
     * Column headers, and the names a record's rail gives its author, assignee and date (the v1
     * field rail, the record placeholder's) whether or not the register shows those columns.
     */
    labels?: Partial<Record<FieldRegisterColumn, string>>;
    statusLabels?: Readonly<Record<string, string>>;
    /** Server filters beyond the status views and the date range. */
    filters: readonly FieldRegisterFilter[];
    /** The panel's names for its `field.<key>` filters. */
    filterLabels?: Readonly<Record<`field.${string}`, string>>;
    /** Workflow field keys every row carries (`fields=`, `ControlSummary.fields`; at most 20). */
    fields?: readonly string[];
    /** Row parts every row carries (`parts=`, HDH §4.3). */
    parts?: readonly string[];
    /**
     * The kind's own tabs (`tab=`, HDH §4.3) as the register's state row, each with the register
     * summary's count; the register opens on `defaultTab`. A kind with tabs chooses its status
     * in the filter panel, unless `statusesInRow`.
     */
    tabs?: readonly { key: FieldRegisterTabKey; label: string }[];
    defaultTab?: FieldRegisterTabKey;
    /**
     * The state row is the tabs, then the statuses with their counts (`statusFacets`): one choice
     * among them, so a status lists every row in it (`tab=all`) and the counts stay those of the
     * whole register (hp 11-01, HBW: "IR list tabs should have Open, Passed, Failed").
     */
    statusesInRow?: true;
    /**
     * Views the filter panel offers as Show (`show=`), each narrowing the state row's choice and
     * its counts as any filter does.
     */
    show?: readonly { key: FieldRegisterTabKey; label: string }[];
    /**
     * The status choices are the register summary's states (`facets.steps`), each sent as its
     * `statuses`; the registry's `statuses` and `statusLabels` are not read.
     */
    statusFacets?: true;
    childRows?: FieldRegisterChildRows;
    /** What the date range filters, and what a dated row's Date column shows. */
    dateLabel: string;
    dateBounds?: "half-open";
    /** Rows tick for the bulk operations the server allows on them. */
    bulk: boolean;
    defaultSort: "number" | "date";
}

/** The drawing marker for a record kind: an icon path (24-unit Material viewBox) and the noun the ARIA label opens with. */
export interface RecordMarker {
    glyph: string;
    noun: string;
}

/** A labelled custom field the dialog composer lays out beside the draft's own fields. */
export interface ComposerDetailField {
    name: string;
    /** The `custom_fields` key written when the draft has none of the aliases yet. */
    key: string;
    /** Keys an imported or older draft may already use for the same value. */
    aliases: readonly string[];
    placeholder?: string;
    type?: "date";
}

/**
 * What the dialog composer (`GenericControlEditor`) shows for a kind: which of the draft's
 * fields it lays out and what it calls them. A kind without a profile gets
 * `GENERIC_DIALOG_COMPOSER`; the composer never compares against a kind.
 */
export interface DialogComposerProfile {
    bodyLabel: string;
    /** The verb in "Saved as a draft until you … it". */
    sendVerb: string;
    dueDate: boolean;
    specSection: boolean;
    purpose: boolean;
    detailFields: readonly ComposerDetailField[];
    /** The people the draft routes to (`participants`); null where the kind routes to no one. */
    routing: { heading: string; label: string; single: boolean; hint?: string } | null;
    /** The kind states where, when and to whom it applies (`applicability`). */
    applicability: boolean;
    filesLabel: string;
    /** The kind cites a source document and page (`source_document_id`, `source_page`). */
    sourceDocument: boolean;
}

/** The field composer's own parts of its main column; "workflow" is the template's fields. */
export type FieldComposerPart = "description" | "workflow" | "location" | "references" | "photos";

/**
 * A named section of the field composer (HDH S9: Issue, Status, Your log columns, …): its
 * `note`, the composer's own `parts`, then the template's fields it names by key, laid out in
 * the server's order, never this list's.
 */
export interface FieldComposerGroup {
    title: string;
    /** One line under the heading (S9's Status: where a new record starts and who moves it). */
    note?: string;
    parts?: readonly Exclude<FieldComposerPart, "workflow">[];
    fields?: readonly string[];
}

/**
 * What the field composer (`PunchComposer`, the `fields` preset) calls its parts for a kind.
 * A kind without a profile gets `GENERIC_FIELD_COMPOSER`.
 */
export interface FieldComposerProfile {
    /** "Create and send" on a fresh draft; an existing draft shows the server's own label. */
    sendLabel: string;
    main?: readonly FieldComposerPart[];
    /**
     * The main column as named sections, in place of `main` (which it makes the composer
     * ignore): the first opens with the title, and a template field no group names joins the
     * last group, so a field the server adds is never lost.
     */
    groups?: readonly FieldComposerGroup[];
    rail?: readonly (
        "assignee" | "due" | "classifier" | "priority" | "rootCause" | "distribution"
    )[];
    bodyRequired?: boolean;
    bodyLabel?: string;
    /** A workflow-driven field record's rail reads labels/values from its pin, never raw keys. */
    workflowRail?: boolean;
    /** The section title and the label new photos are filed under (`ControlPhoto.label`). */
    photos: { title: string; label: "before" | "after" | "photo" };
    /** The draft field that classifies the record, and what the form calls it. */
    classifier: { field: "trade" | "type"; label: string; placeholder: string };
    assigneeRequired: boolean;
    /** The workflow-settings group whose `due_days` seeds a new draft's due date. */
    dueDaysSetting?: "punch_defaults";
    /**
     * The record view's photo sections, one per photo label, in order. `whenPresent` drops a
     * section no photo is filed under. Absent: one section, titled `photos.title`, of every photo.
     */
    photoSections?: readonly {
        label: "before" | "after" | "photo";
        title: string;
        whenPresent?: boolean;
    }[];
}

/** The words a staged kind's Setup steps use by default; see `RecordModule.staged.words`. */
export interface StagedWords {
    submit: string;
    check: string;
    review: string;
    release: string;
}

/** Traits of a native record kind. Static: the server remains the authority for actions and visibility. */
export interface RecordModule {
    register: RecordRegisterPreset;
    body: RecordBodyPreset;
    /** Editing replaces the record view with a page, or opens a modal over it. */
    composer: "page" | "modal";
    /**
     * A new record saves itself as a draft once the author starts writing, and the page composer
     * then lives at the draft's own address (`/documents/<kind>/drafts/:id`, the composer's
     * expanded Marvin view at `…/marvin`), so a reload, a new tab or Back finds the same draft.
     */
    draftPages?: boolean;
    /**
     * A draft carries no number until its workflow gives it one when sent: until then it is named
     * by its subject, never by the internal sequence the server shows meanwhile (`unnumberedDraftName`).
     */
    draftsUnnumbered?: boolean;
    /** Runs a staged review round (`isStagedRecordKind`) without `staged`'s Settings traits. */
    stagedRuntime?: boolean;
    /** False for a system workflow that cannot be inspected or customized through Settings. */
    workflowSettings?: boolean;
    /** Visit Charges (page, register tab, rail row); `tool` is the `effective_tool_permissions` key that gates them. */
    visits?: { tool: string };
    /** Field records: inline action panel, no guest invites, no issued-files section. */
    fieldKind: boolean;
    /** Mirrored provider records (Procore, ACC) list beside the native ones (`?imported=1`). */
    sourceRecords: boolean;
    /**
     * The kind's workspace register pages, sorts and filters its records on the server (the RFI
     * and submittal registers, contract §4.3), so the category never lists the kind in full for it.
     */
    serverPaged?: boolean;
    /** Lists and reviews drawing citations on the record. */
    citations: boolean;
    /** The title band shows "Rev N"; off where the record's number already carries its revision. */
    showsRevision: boolean;
    /**
     * The record view lists the issued manifest as a section of its own, under this title once
     * issued; absent where the body lays out its own files (a submittal's sets, a field record's
     * photos). `hideWhenEmpty` drops the section instead of saying "No files attached".
     */
    files?: { issuedTitle: string; hideWhenEmpty?: boolean };
    /** Offered by the viewer's stamp picker when placing a record on a sheet. */
    stampPicker: boolean;
    marker: RecordMarker;
    /**
     * Workflows (Project settings) target this kind. `release` says whether the
     * manager's closing step can be switched off in a workflow (`optional`) or is part of the
     * kind's workflow (`always`), and `releaseLabel` names that step in the editor.
     * `conditions` is what a step may depend on: the record's spec section, and its priority
     * from these values (compared exactly, so they are the composer's options). `withoutWorkflow`
     * says what happens to a record sent without one: `option` in the composer's picker,
     * `phrase` after the kind's plural noun in a sentence.
     */
    staged?: {
        release: "always" | "optional";
        releaseLabel: string;
        conditions: { spec?: true; priorities?: readonly string[] };
        withoutWorkflow: { option: string; phrase: string };
        /**
         * The kind's own words in Setup, where the workflow has none written yet (a step not named,
         * the check Setup adds): the submitter's step, the GC's check, the review round and the
         * GC's final step. Absent, Setup keeps the RFI's ("Submitted" for an added check).
         */
        words?: StagedWords;
        /**
         * What publishing says a running record keeps ("Submittals already using this workflow
         * keep the version they started with"): who it is about and what they keep. Absent:
         * "RFIs already running keep the steps they started with".
         */
        keeps?: { who: string; what: string };
        /**
         * The project keeps defaults for these records that steps can name (RFIs › Defaults): a
         * manager who can hold a step, a review period an answer step can follow, and the
         * project's distribution a step can copy. Absent (a submittal), steps are held by roles,
         * count days of their own and copy roles or distribution lists.
         */
        projectDefaults?: true;
    };
    /** Register import (CSV/XLSX) accepts this kind. */
    importable?: boolean;
    /**
     * Where an import may land, first the default. Absent: the kind's own records, and the
     * import sends no destination (an RFI import is exactly what it was). A submittal offers its
     * register rows (`requirements`) or its drafts (`records`).
     */
    importDestinations?: readonly ("records" | "requirements")[];
    /** Register status filter vocabulary; absent when the register filters by saved views. */
    statuses?: readonly string[];
    /** Layout of the `field` register; a field kind without one lists No. · Title · Status · Date. */
    fieldRegister?: FieldRegisterLayout;
    /** The dialog composer's fields and words for this kind (`dialogComposer(kind)`). */
    dialogComposer?: DialogComposerProfile;
    /** The field composer's words for this kind (`fieldComposer(kind)`). */
    fieldComposer?: FieldComposerProfile;
    /**
     * The project overview shows this kind's open total as a headline tile, under these words.
     * A kind without it is still reported under "All workflow aging".
     */
    overview?: { label: string; hint: string };
    /** My work offers "New …" for this kind beside its title; every other kind is under More. */
    headlineCreate?: boolean;
    /**
     * The record's number can be a spec-section label that reads on its own ("07 92 00-001"),
     * so lists do not put the kind's name in front of it.
     */
    numberReadsAlone?: boolean;
    /** The workflow ends in an official answer; the reviews report prints question and answer. */
    officialAnswer?: boolean;
    /**
     * A manager may invite an outside party to answer through a guest link. The server decides
     * who may manage the links (`recordCapabilities`); the trait only says the kind has them.
     */
    guestLinks?: boolean;
    /**
     * The kind has its own Project settings section (Project settings › RFIs): its manager, review
     * period, reminders, prefix and default recipients are set there, not in Defaults.
     */
    ownSettingsPage?: boolean;
    /**
     * Marvin drafts records of this kind from a sentence or a file (his RFI draft tools): the
     * register's New offers "Describe it to Marvin" and "From a file…" beside the blank form.
     */
    marvinDrafts?: boolean;
    /**
     * Opening a sheet from the record's reference chips counts as `rfi_view_on_drawing`: the
     * record's drawing, at the spot its RFI links pin or on the whole sheet.
     */
    reportsDrawingOpens?: boolean;
    /**
     * Pages of the kind's own under its register's address (`/documents/<kind>/<page>`), drawn
     * from `CATEGORY_PARTS[body].pages` and offered in the register's ⋯: the inspection
     * request's Summary and Inspection settings.
     */
    subpages?: readonly string[];
    /**
     * The record has no page of its own: its address (`?native=<id>`) opens the record its
     * `kind_view` names (an inspection round inside its request), on the `parent` kind's
     * register, with the record's id under `param` (`?native=<ir>&round=<id>`).
     */
    opensInParent?: { param: string; parent: string };
    /**
     * "File a linked request" on this kind's record (HDH §2.6.10): the kind of the record it
     * starts, carried from this one (`?carry_from=`), offered only where the record's view says the
     * server admits the carry.
     */
    linkedRequest?: { kind: string; label: string };
    /** Layout of the `log` register: its columns in the customer's order (HDH §5.4). */
    logRegister?: LogRegister;
}

export interface DocumentModule {
    label: string;
    singularLabel: string;
    /** The plural inside a sentence, where the label names the collection ("Punch list"). */
    pluralNoun?: string;
    icon: AppIconName;
    route: string;
    group: DocumentModuleGroup;
    order: number;
    requires?: DocumentModuleRequirement;
    /** Tablet shell title when it differs from `label`. */
    tabletTitle?: string;
    children?: readonly DocumentModuleChild[];
    /** Full-canvas modules own their header, tabs, list and record; file categories use the generic scaffold. */
    canvas: "full" | "files";
    count: DocumentCountSource;
    /** Categories that render as tabs inside this one (Contracts). */
    sections?: readonly string[];
    /**
     * The kind lists provider records (ACC, Procore) on every tenant, whether or not Project
     * Management is on; the flag only adds the native records and creation. RFIs, Submittals.
     */
    providerRecords?: true;
    /** Listed even without Project Management, at a legacy route with a legacy count. */
    fallback?: { route: string; count: "distribution" };
    record?: RecordModule;
}

const MARKERS = {
    rfi: {
        glyph: "M7 3H4v3H2V1h5zm15 3V1h-5v2h3v3zM7 21H4v-3H2v5h5zm13-3v3h-3v2h5v-5zM17 6H7v12h10zm2 12c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2zM15 8H9v2h6zm0 3H9v2h6zm0 3H9v2h6z",
        noun: "RFI",
    },
    submittal: {
        glyph: "M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z",
        noun: "Submittal",
    },
    transmittal: { glyph: "M2.01 21 23 12 2.01 3 2 10l15 2-15 2z", noun: "Transmittal" },
    provision: {
        glyph: "M1 21h12v2H1zM5.245 8.07l2.83-2.827 14.14 14.142-2.828 2.828zM12.317 1l5.657 5.656-2.83 2.83-5.654-5.66zM3.825 9.485l5.657 5.657-2.828 2.828-5.657-5.657z",
        noun: "Provision",
    },
    punch: {
        glyph: "M16.59 7.58 10 14.17l-3.59-3.58L5 12l5 5 8-8zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z",
        noun: "Punch item",
    },
    issue: {
        glyph: "M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z",
        noun: "Issue",
    },
    daily_report: {
        glyph: "M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z",
        noun: "Daily report",
    },
    meeting: {
        glyph: "M17 10H7v2h10v-2zm2-7h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zm-5-5H7v2h7v-2z",
        noun: "Meeting",
    },
    form: {
        glyph: "M19 5v14H5V5h14m1.1-2H3.9c-.5 0-.9.4-.9.9v16.2c0 .4.4.9.9.9h16.2c.4 0 .9-.5.9-.9V3.9c0-.5-.5-.9-.9-.9zM11 7h6v2h-6V7zm0 4h6v2h-6v-2zm0 4h6v2h-6zM7 7h2v2H7zm0 4h2v2H7zm0 4h2v2H7z",
        noun: "Form",
    },
    ddnc: {
        glyph: "M16.54 11 13 7.46l1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41zM11 7H2v2h9zm10 6.41L19.59 12 17 14.59 14.41 12 13 13.41 15.59 16 13 18.59 14.41 20 17 17.41 19.59 20 21 18.59 18.41 16zM11 15H2v2h9z",
        noun: "DDNC",
    },
} satisfies Record<string, RecordMarker>;

/**
 * HBW's DDNC log, in its order (HDH §5.4): the `log` register's columns. A field column's header is
 * the template's label (tenant data); its value is the row's `fields` (asked for as `fields=`).
 */
const DDNC_LOG_COLUMNS: readonly LogColumn[] = [
    { key: "number", source: "record", label: "DDNC #", locked: true },
    { key: "permit", source: "field", label: "Permit" },
    {
        key: "installing_company",
        source: "field",
        label: "Contractor",
        hint: "This re-routes the DDNC to QC.",
    },
    { key: "created_on", source: "record", label: "Date Created", locked: true },
    { key: "oshpd_type", source: "field", label: "DDNC Type" },
    { key: "shop_or_field", source: "field", label: "Shop v Field" },
    { key: "description", source: "record", label: "OIL Description" },
    { key: "notes", source: "ddnc", label: "Notes" },
    { key: "prepour", source: "field", label: "Pre-Pour Closure Requirement?" },
    { key: "responsible_party", source: "field", label: "Responsible Party" },
    { key: "court", source: "court", label: "Ball In Court", locked: true },
    { key: "area", source: "field", label: "Area" },
    { key: "discipline", source: "field", label: "Discipline" },
    { key: "gc_lead", source: "field", label: "GC lead" },
    { key: "validity", source: "field", label: "DDNC Validity" },
    { key: "ddnc_status", source: "ddnc", label: "DDNC Status", locked: true },
    { key: "failed_ir_ncr", source: "field", label: "Failed IR/NCR/None" },
    { key: "date_of_completion", source: "field", label: "Date of Completion" },
    { key: "date_of_reinspection", source: "field", label: "Date of Reinspection" },
    { key: "targeted_closure", source: "field", label: "Targeted Closure" },
    { key: "need_by", source: "field", label: "Need By Date" },
    { key: "discuss_in_meeting", source: "field", label: "Discuss in Meeting" },
    { key: "opened_days", source: "court", label: "Days Open", locked: true },
    { key: "linked", source: "ddnc", label: "Linked", locked: true },
];

/** The dialog composer for a kind that declares none: title, description, due date, reviewers, files. */
export const GENERIC_DIALOG_COMPOSER: DialogComposerProfile = {
    bodyLabel: "Description",
    sendVerb: "issue",
    dueDate: true,
    specSection: false,
    purpose: false,
    detailFields: [],
    routing: { heading: "Routing", label: "Reviewers", single: false },
    applicability: false,
    filesLabel: "Project attachments",
    sourceDocument: false,
};

/** The field composer for a kind that declares none: neutral words, nothing required beyond the basics. */
const GENERIC_FIELD_COMPOSER: FieldComposerProfile = {
    sendLabel: "Create and send",
    photos: { title: "Photos", label: "photo" },
    classifier: { field: "type", label: "Type", placeholder: "" },
    assigneeRequired: false,
};

/**
 * The one place a document category or record kind is declared. Presentation, routing,
 * counting, register/body selection and viewer traits all read from here; hooks resolve
 * counts and availability, and the server remains the authority for record visibility
 * and workflow actions. Adding a kind is one entry: the sidebar, routes, filters, tablet
 * titles, record view and stamp picker pick it up without further edits.
 */
const MODULES = {
    overview: {
        label: "Overview",
        singularLabel: "Overview",
        icon: "controlTower",
        route: "/documents",
        group: "overview",
        order: 0,
        canvas: "full",
        count: { source: "none" },
    },
    "my-work": {
        label: "My work",
        singularLabel: "My work",
        icon: "myWork",
        route: "/documents/my-work",
        group: "overview",
        order: 1,
        requires: "projectManagement",
        canvas: "full",
        count: { source: "my-work" },
    },
    reports: {
        label: "Reports",
        singularLabel: "Report",
        icon: "spreadsheet",
        route: "/documents/reports",
        group: "overview",
        order: 2,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "none" },
    },
    form: {
        label: "Forms",
        singularLabel: "Form",
        icon: "requirements",
        route: "/documents/form",
        group: "workflows",
        order: 18,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "native" },
        children: [
            { tab: "templates", label: "Templates" },
            { tab: "expected", label: "Expected submissions" },
        ],
        record: {
            register: "forms",
            body: "form",
            composer: "page",
            fieldKind: true,
            sourceRecords: false,
            citations: true,
            stampPicker: true,
            marker: MARKERS.form,
            showsRevision: true,
            statuses: ["expected", "draft", "submitted", "changes_requested", "accepted", "void"],
        },
    },
    rfi: {
        label: "RFIs",
        singularLabel: "RFI",
        icon: "rfi",
        route: "/documents/rfi",
        providerRecords: true,
        group: "workflows",
        order: 10,
        canvas: "full",
        count: { source: "combined", key: "rfi" },
        record: {
            register: "workspace",
            body: "rfi",
            composer: "page",
            draftPages: true,
            draftsUnnumbered: true,
            fieldKind: false,
            sourceRecords: true,
            serverPaged: true,
            citations: true,
            stampPicker: true,
            marker: MARKERS.rfi,
            overview: { label: "Open RFIs", hint: "Awaiting response or official answer" },
            showsRevision: false,
            files: { issuedTitle: "Question files", hideWhenEmpty: true },
            staged: {
                release: "always",
                releaseLabel: "Manager publishes the official answer",
                conditions: { priorities: PRIORITY_OPTIONS },
                withoutWorkflow: {
                    option: "None: the RFI manager answers",
                    phrase: "go to the RFI manager",
                },
                projectDefaults: true,
            },
            importable: true,
            headlineCreate: true,
            officialAnswer: true,
            guestLinks: true,
            ownSettingsPage: true,
            marvinDrafts: true,
            reportsDrawingOpens: true,
        },
    },
    submittal: {
        label: "Submittals",
        singularLabel: "Submittal",
        icon: "submittal",
        route: "/documents/submittal",
        providerRecords: true,
        group: "workflows",
        order: 11,
        canvas: "full",
        count: { source: "records" },
        children: [
            { tab: "packages", label: "Packages", count: "packages" },
            { tab: "required", label: "Spec register" },
        ],
        record: {
            register: "workspace",
            body: "submittal",
            composer: "page",
            fieldKind: false,
            sourceRecords: true,
            serverPaged: true,
            citations: true,
            stampPicker: true,
            marker: MARKERS.submittal,
            overview: { label: "Submittals in review", hint: "Awaiting review or release" },
            showsRevision: false,
            staged: {
                release: "optional",
                releaseLabel: "Manager releases the final review",
                conditions: { spec: true },
                withoutWorkflow: {
                    option: "None: reviewers by spec section",
                    phrase: "use reviewers by spec section",
                },
                words: {
                    submit: "Submit",
                    check: "GC check",
                    review: "Design team review",
                    release: "GC review & distribution",
                },
                keeps: { who: "already using this workflow", what: "version" },
            },
            importable: true,
            importDestinations: ["requirements", "records"],
            headlineCreate: true,
            numberReadsAlone: true,
            dialogComposer: {
                ...GENERIC_DIALOG_COMPOSER,
                specSection: true,
                detailFields: [
                    {
                        name: "Type",
                        key: "type",
                        aliases: ["type", "item_type", "submittal type"],
                        placeholder: "Product data, shop drawings, sample…",
                    },
                    {
                        name: "Received from",
                        key: "received from",
                        aliases: ["received from", "responsible contractor", "submitter"],
                        placeholder: "Contractor or submitting company",
                    },
                    {
                        name: "Required on site",
                        key: "required on site",
                        aliases: ["required on site", "required_on_site", "required date"],
                        type: "date",
                    },
                ],
                routing: {
                    heading: "Routing",
                    label: "Reviewers, in order",
                    single: false,
                    hint: "Each reviewer receives the package after the previous reviewer approves.",
                },
            },
        },
    },
    transmittal: {
        label: "Transmittals",
        singularLabel: "Transmittal",
        icon: "transmittal",
        route: "/documents/transmittal",
        group: "workflows",
        order: 12,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "native", keys: ["transmittal", "imported_transmittal"] },
        fallback: { route: TRANSMITTALS_PATH, count: "distribution" },
        record: {
            register: "control",
            body: "transmittal",
            composer: "modal",
            fieldKind: false,
            sourceRecords: true,
            citations: false,
            stampPicker: false,
            marker: MARKERS.transmittal,
            showsRevision: false,
            dialogComposer: {
                ...GENERIC_DIALOG_COMPOSER,
                purpose: true,
                routing: { heading: "Routing", label: "Recipients", single: false },
                filesLabel: "Package files",
            },
        },
    },
    punch: {
        label: "Punch list",
        singularLabel: "Punch item",
        pluralNoun: "punch items",
        icon: "punch",
        route: "/documents/punch",
        group: "workflows",
        order: 13,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "native" },
        record: {
            register: "field",
            body: "fields",
            composer: "page",
            fieldKind: true,
            sourceRecords: false,
            citations: true,
            stampPicker: true,
            marker: MARKERS.punch,
            overview: { label: "Open punch items", hint: "Open or ready to verify" },
            showsRevision: true,
            statuses: ["draft", "open", "ready", "closed", "void"],
            fieldComposer: {
                sendLabel: "Open item",
                photos: { title: "Before photos", label: "before" },
                classifier: {
                    field: "trade",
                    label: "Trade",
                    placeholder: "Concrete, electrical, finishes…",
                },
                assigneeRequired: true,
                dueDaysSetting: "punch_defaults",
                photoSections: [
                    { label: "before", title: "Before photos" },
                    { label: "after", title: "After photos" },
                    { label: "photo", title: "Evidence photos", whenPresent: true },
                ],
            },
            fieldRegister: {
                columns: ["trade", "location", "assignee", "due"],
                filters: ["trade", "level", "location"],
                dateLabel: "Issued date",
                bulk: true,
                defaultSort: "number",
            },
        },
    },
    inspection: {
        label: "Inspections",
        singularLabel: "Inspection request",
        icon: "punch",
        route: "/documents/inspection",
        group: "workflows",
        order: 15,
        requires: "inspections",
        canvas: "full",
        count: { source: "native" },
        record: {
            register: "field",
            body: "inspection",
            composer: "page",
            draftPages: true,
            subpages: ["summary", "settings"],
            fieldKind: true,
            stagedRuntime: true,
            workflowSettings: false,
            visits: { tool: "inspections" },
            sourceRecords: false,
            citations: true,
            stampPicker: true,
            statuses: ["draft", "requested", "inspection", "not_ready", "passed", "failed", "void"],
            marker: { ...MARKERS.punch, noun: "Inspection" },
            showsRevision: true,
            headlineCreate: true,
            fieldComposer: {
                sendLabel: "Request inspection",
                photos: { title: "Photos", label: "photo" },
                classifier: { field: "type", label: "Type", placeholder: "" },
                assigneeRequired: false,
                bodyRequired: false,
                bodyLabel: "Requirement",
                main: ["workflow", "description", "location", "photos", "references"],
                rail: [],
                workflowRail: true,
            },
            fieldRegister: {
                columns: [
                    "next_action",
                    "company",
                    "inspector",
                    "type",
                    "requested",
                    "sections",
                    "lbs",
                    "sheets",
                    "flags",
                ],
                // Not column headers: the names the v1 rail (D8, `NativePunchBody`) and the
                // record placeholder's rail (`RecordPlaceholderView`) give these facts.
                labels: { author: "Requested by", assignee: "Inspector", date: "Visit" },
                // HDH §4.3 and S2: the request's facts and rounds; Needs my action, All and the
                // server's states in one row, the request's other views under Show.
                parts: ["inspection", "rounds", "court"],
                tabs: [
                    { key: "ir_mine", label: "Needs my action" },
                    { key: "all", label: "All" },
                ],
                defaultTab: "ir_mine",
                statusFacets: true,
                statusesInRow: true,
                show: [
                    { key: "ir_assigned", label: "Assigned to me" },
                    { key: "ir_delayed", label: "Delayed" },
                    { key: "ir_short_notice", label: "Short notice" },
                    { key: "ir_ongoing", label: "Ongoing" },
                ],
                childRows: "rounds",
                filters: [
                    "show",
                    "field.installing_company",
                    "court",
                    "lbs_prefix",
                    "level",
                    "location",
                ],
                filterLabels: { "field.installing_company": "Installing company" },
                dateLabel: "Visit",
                dateBounds: "half-open",
                bulk: false,
                defaultSort: "number",
            },
        },
    },
    // A round of an inspection request (HDH §2): a record of its own for actions, history and
    // charges, read only inside its request's page.
    inspection_round: {
        label: "Inspection rounds",
        singularLabel: "Inspection round",
        icon: "punch",
        route: "/documents/inspection_round",
        group: "workflows",
        order: 17,
        requires: "inspections",
        canvas: "full",
        count: { source: "none" },
        record: {
            // Never listed on its own: no register page, and nothing reads the kind in full.
            register: "control",
            serverPaged: true,
            body: "inspection",
            composer: "page",
            fieldKind: true,
            workflowSettings: false,
            sourceRecords: false,
            citations: false,
            stampPicker: false,
            showsRevision: false,
            marker: { ...MARKERS.punch, noun: "Inspection round" },
            opensInParent: { param: "round", parent: "inspection" },
        },
    },
    ddnc: {
        label: "DDNC log",
        singularLabel: "DDNC",
        pluralNoun: "DDNCs",
        icon: "ddnc",
        route: "/documents/ddnc",
        group: "workflows",
        order: 16,
        requires: "inspections",
        canvas: "full",
        count: { source: "native" },
        record: {
            register: "log",
            body: "ddnc",
            composer: "page",
            draftPages: true,
            serverPaged: true,
            fieldKind: true,
            workflowSettings: false,
            sourceRecords: false,
            citations: false,
            stampPicker: true,
            showsRevision: false,
            marker: MARKERS.ddnc,
            // HDH §4.3, S7: the DDNC's derived facts, its in-place edits and ball in court, the log's
            // field columns, its views, the server's states and the log's filters.
            fieldRegister: {
                columns: [],
                parts: ["ddnc", "edits", "court"],
                fields: DDNC_LOG_COLUMNS.filter((column) => column.source === "field").map(
                    (column) => column.key,
                ),
                tabs: [
                    { key: "ddnc_open", label: "Open DDNCs" },
                    { key: "ddnc_meeting", label: "Discuss in meeting" },
                    { key: "closed", label: "Closed" },
                    { key: "all", label: "All DDNCs" },
                ],
                defaultTab: "ddnc_open",
                statusFacets: true,
                filters: ["field.oshpd_type", "field.installing_company", "court", "ddnc_area"],
                filterLabels: {
                    "field.oshpd_type": "DDNC type",
                    "field.installing_company": "Contractor",
                },
                dateLabel: "Created",
                bulk: false,
                defaultSort: "number",
            },
            logRegister: {
                columns: DDNC_LOG_COLUMNS,
                // "+ Add row" (HBW QC): the draft is logged with only its contractor (HDH §2.4.2).
                addRow: { action: "log_row", field: "installing_company", title: "New DDNC" },
                footerTabs: [
                    { key: "ddnc_open", word: "open" },
                    { key: "closed", word: "closed" },
                ],
            },
            linkedRequest: { kind: "inspection", label: "File a linked request" },
            // S9's groups over the template's fields (`definitions_ddnc.FIELDS`), with the
            // description, the pin or "off-site" and the linked records.
            fieldComposer: {
                sendLabel: "Log DDNC",
                photos: { title: "Photos", label: "photo" },
                classifier: { field: "type", label: "Type", placeholder: "" },
                assigneeRequired: false,
                bodyRequired: true,
                bodyLabel: "OIL Description",
                groups: [
                    {
                        title: "Issue",
                        parts: ["description"],
                        fields: [
                            "issue_type",
                            "oshpd_type",
                            "reason",
                            "priority",
                            "permit",
                            "tio_line",
                            "vcr_milestone",
                        ],
                    },
                    {
                        title: "Status",
                        note: "A new DDNC starts at New with QC. QC assigns it; only the IOR closes it.",
                    },
                    {
                        title: "Your log columns",
                        fields: [
                            "shop_or_field",
                            "prepour",
                            "area",
                            "discipline",
                            "gc_lead",
                            "failed_ir_ncr",
                            "targeted_closure",
                            "need_by",
                            "discuss_in_meeting",
                        ],
                    },
                    {
                        title: "Companies and people",
                        fields: ["installing_company", "responsible_party", "assignment_comments"],
                    },
                    {
                        title: "Location",
                        parts: ["location"],
                        fields: ["lbs_codes", "location_text"],
                    },
                    { title: "Related items", parts: ["references"] },
                    // What the closing path fills in (DDNC Validity, the completion and reinspection
                    // dates, How it closes, the paperwork), and any field the server adds.
                    { title: "More columns" },
                ],
                rail: [],
                workflowRail: true,
            },
        },
    },
    issue: {
        label: "Issues",
        singularLabel: "Issue",
        icon: "issue",
        route: "/documents/issue",
        group: "workflows",
        order: 14,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "native" },
        record: {
            register: "field",
            body: "fields",
            composer: "page",
            fieldKind: true,
            sourceRecords: true,
            citations: true,
            stampPicker: true,
            marker: MARKERS.issue,
            overview: { label: "Open issues", hint: "Open or resolved, awaiting closure" },
            showsRevision: true,
            statuses: ["draft", "open", "resolved", "closed", "void"],
            fieldComposer: {
                sendLabel: "Raise issue",
                photos: { title: "Evidence photos", label: "photo" },
                classifier: {
                    field: "type",
                    label: "Type",
                    placeholder: "Coordination, quality, safety…",
                },
                assigneeRequired: false,
            },
            fieldRegister: {
                columns: ["type", "location", "assignee", "due"],
                filters: ["type", "level", "location"],
                dateLabel: "Issued date",
                bulk: true,
                defaultSort: "number",
            },
        },
    },
    daily_report: {
        label: "Daily reports",
        singularLabel: "Daily report",
        icon: "dailyReport",
        route: "/documents/daily_report",
        group: "workflows",
        order: 15,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "native" },
        record: {
            register: "field",
            body: "dailyReport",
            composer: "page",
            fieldKind: true,
            sourceRecords: true,
            citations: false,
            stampPicker: false,
            marker: MARKERS.daily_report,
            showsRevision: true,
            statuses: ["draft", "submitted", "approved", "void"],
            fieldRegister: {
                columns: ["date", "workers", "hours", "author"],
                filters: [],
                dateLabel: "Report date",
                bulk: false,
                defaultSort: "date",
            },
        },
    },
    meeting: {
        label: "Meetings",
        singularLabel: "Meeting",
        icon: "meeting",
        route: "/documents/meeting",
        group: "workflows",
        order: 16,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "native" },
        children: [{ tab: "templates", label: "Templates" }],
        record: {
            register: "field",
            body: "meeting",
            composer: "page",
            fieldKind: true,
            sourceRecords: false,
            citations: false,
            stampPicker: false,
            marker: MARKERS.meeting,
            showsRevision: true,
            statuses: ["draft", "published", "void"],
            fieldRegister: {
                columns: ["date", "attendees", "open_actions"],
                filters: [],
                dateLabel: "Meeting date",
                bulk: false,
                defaultSort: "date",
            },
        },
    },
    provision: {
        label: "Provisions",
        singularLabel: "Provision",
        icon: "supplemental",
        route: "/documents/provision",
        group: "workflows",
        order: 17,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "native" },
        record: {
            register: "control",
            body: "provision",
            composer: "modal",
            fieldKind: false,
            sourceRecords: false,
            citations: false,
            stampPicker: false,
            marker: MARKERS.provision,
            showsRevision: true,
            files: { issuedTitle: "Issued files" },
            dialogComposer: {
                ...GENERIC_DIALOG_COMPOSER,
                bodyLabel: "Provision",
                sendVerb: "publish",
                dueDate: false,
                specSection: true,
                routing: null,
                applicability: true,
                sourceDocument: true,
            },
        },
    },
    sheets: {
        label: "Drawings",
        singularLabel: "Drawing",
        icon: "drawing",
        route: "/documents/sheets",
        group: "library",
        order: 20,
        canvas: "files",
        count: { source: "legacy", key: "sheets" },
    },
    specifications: {
        label: "Specifications",
        singularLabel: "Specification",
        icon: "specification",
        route: "/documents/specifications",
        group: "library",
        order: 21,
        canvas: "files",
        count: { source: "legacy", key: "specifications" },
    },
    photos: {
        label: "Photos",
        singularLabel: "Photo",
        icon: "photos",
        route: "/documents/photos",
        group: "library",
        order: 22,
        requires: "primepointRecords",
        canvas: "full",
        count: { source: "photos" },
    },
    contracts: {
        label: "Contracts",
        singularLabel: "Contract",
        icon: "contract",
        route: "/documents/contracts",
        group: "library",
        order: 23,
        canvas: "files",
        count: { source: "legacy", key: "contracts", withSections: true },
        sections: ["primecontract", "code", "supplemental", "ifoa"],
    },
    schedule: {
        label: "Schedule",
        singularLabel: "Schedule",
        icon: "schedule",
        route: "/documents/schedule",
        group: "library",
        order: 24,
        canvas: "files",
        count: { source: "legacy", key: "schedule" },
    },
    documents: {
        label: "Files",
        singularLabel: "File",
        icon: "folder",
        route: "/documents/documents",
        group: "library",
        order: 25,
        tabletTitle: "Documents",
        canvas: "files",
        count: { source: "folders" },
    },
    "acc-files": {
        label: "Autodesk Files",
        singularLabel: "Autodesk file",
        icon: "folder",
        route: "/documents/acc-files",
        group: "library",
        order: 26,
        tabletTitle: "Autodesk Forma Files",
        canvas: "files",
        count: { source: "provider" },
    },
    "procore-files": {
        label: "Procore Files",
        singularLabel: "Procore file",
        icon: "folder",
        route: "/documents/procore-files",
        group: "library",
        order: 27,
        canvas: "files",
        count: { source: "provider" },
    },
    "sharepoint-files": {
        label: "SharePoint Files",
        singularLabel: "SharePoint file",
        icon: "folder",
        route: "/documents/sharepoint-files",
        group: "library",
        order: 28,
        canvas: "files",
        count: { source: "provider" },
    },
    code: {
        label: "Codes",
        singularLabel: "Code",
        icon: "code",
        route: "/documents/code",
        group: "library",
        order: 30,
        canvas: "files",
        count: { source: "legacy", key: "code" },
    },
    ifoa: {
        label: "IFOA",
        singularLabel: "IFOA",
        icon: "ifoa",
        route: "/documents/ifoa",
        group: "library",
        order: 31,
        requires: "ifoa",
        canvas: "files",
        count: { source: "legacy", key: "ifoa" },
    },
    primecontract: {
        label: "Prime Contract",
        singularLabel: "Prime Contract",
        icon: "primeContract",
        route: "/documents/primecontract",
        group: "library",
        order: 32,
        requires: "primeContract",
        canvas: "files",
        count: { source: "legacy", key: "primeContract" },
    },
    supplemental: {
        label: "Supplemental",
        singularLabel: "Supplemental",
        icon: "supplemental",
        route: "/documents/supplemental",
        group: "library",
        order: 33,
        canvas: "files",
        count: { source: "legacy", key: "supplemental" },
    },
} satisfies Record<string, DocumentModule>;

/** Every routable `/documents/:category`. */
export type DocumentCategory = Exclude<keyof typeof MODULES, "overview">;
/** Categories that are native record kinds. */
export type ControlKind = {
    [K in DocumentCategory]: (typeof MODULES)[K] extends { record: RecordModule } ? K : never;
}[DocumentCategory];
/** Kinds staged approval templates target. */
export type StagedKind = {
    [K in ControlKind]: (typeof MODULES)[K] extends { record: { staged: object } } ? K : never;
}[ControlKind];
/** Kinds register import accepts. */
export type ImportableKind = {
    [K in ControlKind]: (typeof MODULES)[K] extends { record: { importable: true } } ? K : never;
}[ControlKind];

/** Kinds whose mirrored provider records list beside the native ones (`?imported=1`). */
export type SourceRecordKind = {
    [K in ControlKind]: (typeof MODULES)[K] extends { record: { sourceRecords: true } } ? K : never;
}[ControlKind];

export const DOCUMENT_MODULES: Record<DocumentCategory | "overview", DocumentModule> = MODULES;

const moduleFor = (id: string): DocumentModule | undefined =>
    (DOCUMENT_MODULES as Record<string, DocumentModule | undefined>)[id];

/** Existing-record routes need the runtime, not the stricter discovery/creation gate. */
export function documentRuntimeRequirement(
    category: DocumentCategory,
): Exclude<DocumentModuleRequirement, "inspections"> | undefined {
    const requirement = DOCUMENT_MODULES[category].requires;
    return requirement === "inspections" ? "primepointRecords" : requirement;
}

/** The tenant facts a module's requirement is decided on (`useModuleGates`). */
export interface ModuleGates {
    /** The Project Management workspace is on. */
    workspace: boolean;
    /** Primepoint is the system of record. */
    records: boolean;
    /** The record kinds the project's provider takes now. */
    providerKinds: readonly string[];
}

/**
 * Whether the workspace or system-of-record requirement of `category` is met: the client's read
 * of ai_engine's rule (`external_owner.records_writable`). A native module needs the system of
 * record inside the workspace; a kind the provider takes needs the workspace alone. Any other
 * requirement is not this function's (`ifoa`, `primeContract`): true here.
 */
export function moduleRequirementMet(category: DocumentCategory, gates: ModuleGates): boolean {
    const requirement = documentRuntimeRequirement(category);
    if (requirement === "projectManagement") return gates.workspace;
    if (requirement === "primepointRecords")
        return gates.workspace && (gates.records || gates.providerKinds.includes(category));
    return true;
}

/** The kind shows provider records on every tenant (no Project Management needed). */
export const listsProviderRecords = (id: string): boolean =>
    moduleFor(id)?.providerRecords === true;

/** Routable categories in registry order (not presentation order). */
export const documentCategories = (): DocumentCategory[] =>
    (Object.keys(DOCUMENT_MODULES) as (DocumentCategory | "overview")[]).filter(
        (id): id is DocumentCategory => id !== "overview",
    );
export const isDocumentCategory = (value: string): value is DocumentCategory =>
    value !== "overview" && !!moduleFor(value);
export const isControlKind = (value: string): value is ControlKind => !!moduleFor(value)?.record;
export const controlKinds = (): ControlKind[] => documentCategories().filter(isControlKind);
export const isStagedKind = (kind: string): kind is StagedKind => !!moduleFor(kind)?.record?.staged;
export const stagedKinds = (): StagedKind[] => controlKinds().filter(isStagedKind);
/** Runtime review fields are independent of whether a kind has a customizable settings page. */
export const isStagedRecordKind = (kind: string): boolean =>
    !!moduleFor(kind)?.record?.staged || moduleFor(kind)?.record?.stagedRuntime === true;
export const supportsWorkflowSettings = (kind: string): boolean =>
    moduleFor(kind)?.record?.workflowSettings !== false;
/** Register import (CSV / XLSX) accepts this kind; the category page offers it from its menu. */
export const isImportableKind = (kind: string): kind is ImportableKind =>
    !!moduleFor(kind)?.record?.importable;
export const isSourceRecordKind = (kind: string): kind is SourceRecordKind =>
    !!moduleFor(kind)?.record?.sourceRecords;
/**
 * Whether Marvin reads the record first at this send: the workflow's own declaration
 * (`send_check` on the action definition of a saved draft, or on the composer profile's `send`
 * for a new one). Never decided by action name.
 */
export const runsSendCheck = (send: { send_check: boolean } | null | undefined): boolean =>
    send?.send_check === true;
/** Marvin drafts this kind from a description or a file (`RecordModule.marvinDrafts`). */
export const isMarvinDraftedKind = (kind: string): boolean =>
    !!moduleFor(kind)?.record?.marvinDrafts;
/**
 * What a kind this client has no entry for renders with: the generic field body and register,
 * server labels and server actions, no kind-specific traits. A server may ship a kind one
 * release ahead of the client (or the client may be rolled back), and opening such a record
 * from My work, search or a report link must show it, not blank the page.
 */
export const UNKNOWN_RECORD_MODULE: RecordModule = {
    register: "field",
    body: "fields",
    composer: "page",
    fieldKind: true,
    sourceRecords: false,
    citations: false,
    stampPicker: false,
    showsRevision: true,
    marker: MARKERS.rfi,
};
/** The record traits of a kind; a kind the registry does not know gets the generic traits. */
export const recordModule = (kind: string): RecordModule =>
    moduleFor(kind)?.record ?? UNKNOWN_RECORD_MODULE;
/** The dialog composer's profile for a kind; the generic one for a kind that declares none. */
export const dialogComposer = (kind: string): DialogComposerProfile =>
    recordModule(kind).dialogComposer ?? GENERIC_DIALOG_COMPOSER;
/** The field composer's profile for a kind; the generic one for a kind that declares none. */
export const fieldComposer = (kind: string): FieldComposerProfile =>
    recordModule(kind).fieldComposer ?? GENERIC_FIELD_COMPOSER;
/** Kinds with a headline tile on the project overview, in the rail's order. */
export const overviewKinds = (): ControlKind[] =>
    controlKinds()
        .filter((kind) => !!recordModule(kind).overview)
        .sort((a, b) => DOCUMENT_MODULES[a].order - DOCUMENT_MODULES[b].order);
/** Kinds My work offers "New …" for beside its title, in registry order. */
export const headlineCreateKinds = (): ControlKind[] =>
    controlKinds().filter((kind) => !!recordModule(kind).headlineCreate);
/** The registered marker for a record kind, or the RFI marker for a kind this client has no entry for. */
export const recordMarker = (kind: string): RecordMarker => recordModule(kind).marker;
/** Sentence-case words for a server identifier the client has no label for (`site_walk` → "Site walk"). */
const humanize = (value: string) => {
    const words = value.replace(/[_-]+/g, " ").trim();
    return words ? words[0].toUpperCase() + words.slice(1) : "Record";
};
/** A kind's plural and singular labels; a kind the registry does not know reads as its own name. */
export const kindLabel = (kind: string): string => moduleFor(kind)?.label ?? humanize(kind);
export const kindSingularLabel = (kind: string): string =>
    moduleFor(kind)?.singularLabel ?? humanize(kind);
/** A kind's module glyph; a kind the registry does not know has the generic one. */
export const kindIcon = (kind: string): AppIconName => moduleFor(kind)?.icon ?? "workflowStudio";
/**
 * "New RFI", "New punch item": the singular label after a verb. A label that is an acronym
 * (all capitals) keeps its case; every other label is lower-cased into the sentence.
 */
export const kindNoun = (kind: string): string => {
    const label = kindSingularLabel(kind);
    return label === label.toUpperCase() ? label : label.toLowerCase();
};
/**
 * "RFIs", "punch items": the plural noun inside a sentence. A module whose label names the
 * collection declares its `pluralNoun`; otherwise the label reads by the same acronym rule.
 */
export const kindPluralNoun = (kind: string): string => {
    const declared = moduleFor(kind)?.pluralNoun;
    if (declared) return declared;
    const singular = kindSingularLabel(kind);
    return singular === singular.toUpperCase() ? kindLabel(kind) : kindLabel(kind).toLowerCase();
};
export const newKindLabel = (kind: string): string => `New ${kindNoun(kind)}`;

/**
 * The Documents rail without Project Management: the categories in the order they have
 * always been listed, contract-family categories as rows of their own, and the folder
 * library under its original name. Project Management regroups the rail (Library,
 * Workflows, Contracts tabs); a tenant without it keeps this one unchanged.
 */
export const LEGACY_CATEGORY_ORDER = [
    "sheets",
    "specifications",
    "rfi",
    "submittal",
    "contracts",
    "code",
    "ifoa",
    "primecontract",
    "supplemental",
    "schedule",
] as const satisfies readonly DocumentCategory[];
export const LEGACY_LIBRARY_LABEL = "Additional docs";
/** Page titles of the file categories without Project Management, where they differ from `label`. */
export const LEGACY_CATEGORY_TITLES: Partial<Record<DocumentCategory, string>> = {
    code: "Building Codes",
    documents: "Documents",
    "acc-files": "Autodesk Forma Files",
};

export const DOCUMENT_GROUP_LABELS = { workflows: "Workflows", library: "Library" } as const;
export const DOCUMENT_GROUP_ORDER = ["library", "workflows"] as const;
export const DOCUMENTS_DESIGN_KIT_PATH = "/documents/design-kit";

/** A missing count must not masquerade as zero; bounded queues show a lower bound. */
export function documentCountLabel(count: number | null, partial = false): string {
    return count == null ? "—" : `${count.toLocaleString()}${partial ? "+" : ""}`;
}
